import { useState, useRef, useMemo } from "react";
import { api } from "../lib/api";
import { Microphone, Stop, X, Quotes } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import RichEditor from "./RichEditor";
import { promptForToday } from "../lib/prompts";

const MOODS = ["Calm", "Happy", "Reflective", "Anxious", "Grateful", "Restless"];

export default function NewEntryModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [mood, setMood] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [promptDismissed, setPromptDismissed] = useState(false);
  const dailyPrompt = useMemo(() => promptForToday(), []);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const usePrompt = () => {
    const html = `<p><em>${dailyPrompt}</em></p><p></p>`;
    setContent((c) => (c ? c + html : html));
    setPromptDismissed(true);
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        const form = new FormData();
        form.append("file", blob, "voice.webm");
        try {
          const r = await api.post("/transcribe", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const paragraph = `<p>${r.data.text.replace(/</g, "&lt;")}</p>`;
          setContent((c) => (c ? c + paragraph : paragraph));
          toast.success("Voice transcribed");
        } catch (e) {
          toast.error("Transcription failed");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stopRec = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  const parseTags = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    if (!content.trim() && !title.trim()) {
      toast.error("Write something first");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/entries", {
        title, content, mood, tags: parseTags(tagsInput),
      });
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="new-entry-modal"
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-md flex items-center justify-center p-4"
    >
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto shadow-lg fade-up">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="font-serif text-2xl">New entry</h2>
          <button
            data-testid="close-new-entry"
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {!promptDismissed && !content && (
            <div
              data-testid="daily-prompt"
              className="rounded-lg border border-accent/30 bg-accent/5 p-4 flex items-start gap-3"
            >
              <Quotes size={20} weight="duotone" className="text-accent shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">
                  Today's prompt
                </p>
                <p className="font-serif text-lg italic">{dailyPrompt}</p>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button
                  data-testid="use-prompt-btn"
                  onClick={usePrompt}
                  className="text-xs uppercase tracking-[0.2em] font-semibold text-accent hover:underline whitespace-nowrap"
                >
                  Use it
                </button>
                <button
                  data-testid="dismiss-prompt-btn"
                  onClick={() => setPromptDismissed(true)}
                  className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground whitespace-nowrap"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          <Input
            data-testid="entry-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="A title, if you feel like one"
            className="lined-input rounded-none px-0 text-xl font-serif focus-visible:ring-0"
          />

          <RichEditor
            value={content}
            onChange={setContent}
            placeholder="What happened today?"
            autoFocus
          />

          <Input
            data-testid="entry-tags-input"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="tags, comma, separated"
            className="lined-input rounded-none px-0 text-sm focus-visible:ring-0"
          />

          <div className="flex flex-wrap gap-2">
            {MOODS.map((m) => (
              <button
                key={m}
                data-testid={`mood-${m.toLowerCase()}`}
                onClick={() => setMood(m === mood ? "" : m)}
                className={`text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full border transition-colors ${
                  mood === m
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:border-primary"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-border bg-secondary/40 sticky bottom-0">
          <button
            data-testid={recording ? "stop-record-btn" : "start-record-btn"}
            onClick={recording ? stopRec : startRec}
            disabled={transcribing}
            className={`flex items-center gap-2 px-5 py-3 rounded-full border transition-colors ${
              recording ? "bg-accent text-accent-foreground border-accent recording" : "border-border hover:border-accent"
            } disabled:opacity-60`}
          >
            {recording ? <Stop size={16} weight="fill" /> : <Microphone size={16} weight="duotone" />}
            <span className="text-xs uppercase tracking-[0.2em] font-semibold">
              {transcribing ? "Transcribing…" : recording ? "Stop" : "Voice"}
            </span>
          </button>

          <Button
            data-testid="save-entry-btn"
            onClick={save}
            disabled={saving}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8"
          >
            {saving ? "Saving…" : "Save entry"}
          </Button>
        </div>
      </div>
    </div>
  );
}
